import React, { useEffect, useState } from 'react';
import { api, AED } from '../lib/api';
import { Plus, Loader2, Receipt as ReceiptIcon } from 'lucide-react';
import InvoiceModal from '../components/InvoiceModal';
import VendorModal from '../components/VendorModal';

export default function Accounting() {
  const [invoices, setInvoices] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [props, setProps] = useState([]);
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [adding, setAdding] = useState(false);
  const [addingExp, setAddingExp] = useState(false);
  const [form, setForm] = useState({ property_id: '', vendor_id: '', description: '', amount_aed: 0, vat_aed: 0, retention_held_aed: 0, invoice_number: '', apply_vat: false, vat_inclusive: false });
  const [expForm, setExpForm] = useState({ property_id: '', amount_aed: 0, category: 'petty_cash', description: '' });
  const [busy, setBusy] = useState(false);
  const [viewInv, setViewInv] = useState(null);
  const [viewVendor, setViewVendor] = useState(null);
  // Filters — vendor, property, status. Kept in state so users can layer them.
  const [fVendor, setFVendor] = useState('');
  const [fProperty, setFProperty] = useState('');
  const [fStatus, setFStatus] = useState('');

  const load = async () => {
    const [i, v, p, pm, ex] = await Promise.all([
      api.get('/invoices'), api.get('/vendors'), api.get('/properties'), api.get('/payments'), api.get('/expenses')
    ]);
    setInvoices(i.data); setVendors(v.data); setProps(p.data); setPayments(pm.data); setExpenses(ex.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post('/invoices', form);
      setAdding(false);
      setForm({ property_id: '', vendor_id: '', description: '', amount_aed: 0, vat_aed: 0, retention_held_aed: 0, invoice_number: '', apply_vat: false, vat_inclusive: false });
      await load();
    } finally { setBusy(false); }
  };

  const submitExp = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post('/expenses', expForm);
      setAddingExp(false);
      setExpForm({ property_id: '', amount_aed: 0, category: 'petty_cash', description: '' });
      await load();
    } finally { setBusy(false); }
  };

  const totalPaid = payments.reduce((s, p) => s + (p.amount_aed || 0), 0);
  const totalPending = invoices.filter(i => i.status?.startsWith('pending')).reduce((s, i) => s + (i.net_payable_aed || i.amount_aed || 0), 0);
  const totalExp = expenses.reduce((s, e) => s + (e.amount_aed || 0), 0);

  // Filtered view — applied to the "All invoices" table below.
  const filteredInvoices = invoices.filter(inv => {
    if (fVendor && inv.vendor_id !== fVendor) return false;
    if (fProperty && inv.property_id !== fProperty) return false;
    if (fStatus && inv.status !== fStatus) return false;
    return true;
  });
  const filtersActive = fVendor || fProperty || fStatus;

  return (
    <div className="py-6 max-w-6xl mx-auto space-y-6" data-testid="accounting-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">Finance</div>
          <h1 className="font-serif text-4xl">Accounting</h1>
        </div>
        <button onClick={()=>setAdding(true)} data-testid="new-invoice-btn" className="btn-primary text-sm flex items-center gap-2"><Plus size={14}/> New invoice</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="glass p-5"><div className="overline">Total paid</div><div className="font-serif text-3xl mt-2 text-emerald-700">{AED(totalPaid)}</div></div>
        <div className="glass p-5"><div className="overline">In approval workflow</div><div className="font-serif text-3xl mt-2 text-amber-700">{AED(totalPending)}</div></div>
        <div className="glass p-5"><div className="overline">Site / petty cash</div><div className="font-serif text-3xl mt-2">{AED(totalExp)}</div></div>
        <div className="glass p-5"><div className="overline">Invoices count</div><div className="font-serif text-3xl mt-2">{invoices.length}</div></div>
      </div>

      <div className="glass p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="font-serif text-2xl">All invoices</div>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={fProperty} onChange={(e) => setFProperty(e.target.value)}
                    data-testid="acc-filter-property"
                    className="px-3 py-1.5 rounded-full border border-slate-200 bg-white/80 text-xs">
              <option value="">All properties</option>
              {props.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
            <select value={fVendor} onChange={(e) => setFVendor(e.target.value)}
                    data-testid="acc-filter-vendor"
                    className="px-3 py-1.5 rounded-full border border-slate-200 bg-white/80 text-xs">
              <option value="">All vendors</option>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.company || v.name}</option>)}
            </select>
            <select value={fStatus} onChange={(e) => setFStatus(e.target.value)}
                    data-testid="acc-filter-status"
                    className="px-3 py-1.5 rounded-full border border-slate-200 bg-white/80 text-xs">
              <option value="">Any status</option>
              <option value="pending_pm">Pending PM</option>
              <option value="pending_md">Pending MD</option>
              <option value="pending_payments">Pending payments</option>
              <option value="paid">Paid</option>
              <option value="rejected">Rejected</option>
            </select>
            {filtersActive && (
              <button onClick={() => { setFVendor(''); setFProperty(''); setFStatus(''); }}
                      data-testid="acc-filter-clear"
                      className="text-xs text-slate-500 hover:text-slate-900 underline">
                Clear
              </button>
            )}
          </div>
        </div>
        {filtersActive && (
          <div className="text-xs text-slate-500 mb-2">
            Showing {filteredInvoices.length} of {invoices.length} invoices
            {' · '}
            Total {AED(filteredInvoices.reduce((s, i) => s + (i.net_payable_aed || i.amount_aed || 0), 0))}
          </div>
        )}
        <table className="w-full text-sm">
          <thead>
            <tr className="overline text-left border-b border-slate-200/60">
              <th className="py-2">#</th><th>Property</th><th>Vendor</th><th>Description</th><th className="text-right">Amount</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.map(inv => {
              const prop = props.find(p => p.id === inv.property_id);
              const vend = vendors.find(v => v.id === inv.vendor_id);
              return (
                <tr key={inv.id} onClick={() => setViewInv(inv.id)} data-testid={`acc-inv-row-${inv.id}`}
                    className="border-b border-slate-100/70 hover:bg-white/50 cursor-pointer">
                  <td className="py-3 font-mono text-xs">{inv.invoice_number}</td>
                  <td className="text-xs">{prop?.title?.slice(0, 30) || '—'}</td>
                  <td className="text-xs">
                    {vend ? (
                      <button onClick={(e) => { e.stopPropagation(); setViewVendor(vend.id); }} className="hover:underline text-[#679286]">
                        {vend.company}
                      </button>
                    ) : '—'}
                  </td>
                  <td className="max-w-xs truncate">{inv.description}</td>
                  <td className="text-right">{AED(inv.amount_aed)}</td>
                  <td>
                    <span className={`status-pill ${inv.status === 'paid' ? 'status-success' : inv.status === 'rejected' ? 'status-danger' : 'status-warning'}`}>
                      {inv.status?.replace(/_/g, ' ')}
                    </span>
                  </td>
                </tr>
              );
            })}
            {filteredInvoices.length === 0 && (
              <tr><td colSpan={6} className="py-8 text-center text-slate-400">
                {invoices.length === 0 ? 'No invoices.' : 'No invoices match the current filters.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="glass p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><ReceiptIcon size={18}/><div className="font-serif text-2xl">Site expenses / petty cash</div></div>
          <button onClick={()=>setAddingExp(true)} data-testid="new-expense-btn" className="btn-primary text-xs flex items-center gap-1"><Plus size={12}/> Log expense</button>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="overline text-left border-b border-slate-200/60"><th className="py-2">Date</th><th>Property</th><th>Category</th><th>Description</th><th className="text-right">Amount</th></tr></thead>
          <tbody>
            {expenses.map(ex => {
              const prop = props.find(p => p.id === ex.property_id);
              return (
                <tr key={ex.id} className="border-b border-slate-100/70">
                  <td className="py-3 text-xs">{new Date(ex.expense_date).toLocaleDateString()}</td>
                  <td className="text-xs">{prop?.title?.slice(0, 30) || '—'}</td>
                  <td><span className="status-pill status-neutral text-xs">{ex.category?.replace('_',' ')}</span></td>
                  <td className="max-w-md truncate">{ex.description}</td>
                  <td className="text-right">{AED(ex.amount_aed)}</td>
                </tr>
              );
            })}
            {expenses.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-slate-400">No expenses logged.</td></tr>}
          </tbody>
        </table>
      </div>

      {adding && (
        <div className="fixed inset-0 z-40 grid place-items-center p-4 bg-slate-900/30 backdrop-blur-sm" onClick={()=>setAdding(false)}>
          <form onClick={(e)=>e.stopPropagation()} onSubmit={submit} className="glass-strong w-full max-w-md p-8" data-testid="new-invoice-modal">
            <div className="overline mb-2">Raise invoice</div>
            <h2 className="font-serif text-2xl mb-4">New invoice</h2>
            <label className="block text-sm mb-2">Property
              <select required value={form.property_id} onChange={(e)=>setForm({...form, property_id:e.target.value})} data-testid="inv-property"
                      className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80">
                <option value="">Choose…</option>
                {props.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </label>
            <label className="block text-sm mb-2">Vendor
              <select required value={form.vendor_id} onChange={(e)=>setForm({...form, vendor_id:e.target.value})} data-testid="inv-vendor"
                      className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80">
                <option value="">Choose…</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.company}</option>)}
              </select>
            </label>
            <label className="block text-sm mb-2">Description
              <input required value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} data-testid="inv-desc"
                     className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
            </label>
            <div className="grid grid-cols-3 gap-2">
              <label className="block text-sm">Amount (AED)
                <input type="number" required value={form.amount_aed} onChange={(e)=>setForm({...form, amount_aed:+e.target.value})} data-testid="inv-amount"
                       className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
              </label>
              <label className="block text-sm">VAT (AED)
                <input type="number" value={form.vat_aed} onChange={(e)=>setForm({...form, vat_aed:+e.target.value})}
                       disabled={form.apply_vat}
                       title={form.apply_vat ? 'Auto-calculated on submit — untick "with VAT" to edit manually' : ''}
                       className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80 disabled:bg-slate-50 disabled:text-slate-400"/>
              </label>
              <label className="block text-sm">Retention (AED)
                <input type="number" value={form.retention_held_aed} onChange={(e)=>setForm({...form, retention_held_aed:+e.target.value})}
                       className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
              </label>
            </div>
            {/* VAT toggle — UAE standard is 5% commercial, 0% residential.
                Backend resolves the rate from the linked property's asset class. */}
            <div className="mt-3 p-3 rounded-2xl bg-slate-50/70 border border-slate-200 space-y-2">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={form.apply_vat}
                       onChange={(e)=>setForm({...form, apply_vat: e.target.checked, vat_aed: 0})}
                       data-testid="inv-apply-vat"
                       className="w-4 h-4 accent-[#679286]"/>
                <span>Invoice includes VAT (auto-calculate)</span>
              </label>
              {form.apply_vat && (
                <label className="flex items-center gap-2 text-xs text-slate-600 ml-6">
                  <input type="checkbox" checked={form.vat_inclusive}
                          onChange={(e)=>setForm({...form, vat_inclusive: e.target.checked})}
                          data-testid="inv-vat-inclusive"
                          className="w-3.5 h-3.5 accent-[#679286]"/>
                  <span>Amount above is already VAT-inclusive (gross). Untick if the amount is net + VAT on top.</span>
                </label>
              )}
              {form.apply_vat && (
                <div className="text-[11px] text-slate-500 ml-6">
                  UAE rate: 5% commercial, 0% residential — auto-picked from the linked property.
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={()=>setAdding(false)} className="btn-ghost text-sm">Cancel</button>
              <button disabled={busy} type="submit" className="btn-primary text-sm" data-testid="inv-submit">
                {busy ? <><Loader2 size={14} className="animate-spin inline mr-1"/> Submitting…</> : 'Submit for approval'}
              </button>
            </div>
          </form>
        </div>
      )}
      {addingExp && (
        <div className="fixed inset-0 z-40 grid place-items-center p-4 bg-slate-900/30 backdrop-blur-sm" onClick={()=>setAddingExp(false)}>
          <form onClick={(e)=>e.stopPropagation()} onSubmit={submitExp} className="glass-strong w-full max-w-md p-6" data-testid="expense-modal">
            <div className="font-serif text-2xl mb-4">Log site expense</div>
            <label className="block text-sm mb-2">Property
              <select required value={expForm.property_id} onChange={(e)=>setExpForm({...expForm, property_id:e.target.value})} data-testid="exp-property"
                      className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80">
                <option value="">Choose…</option>
                {props.map(p=><option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </label>
            <label className="block text-sm mb-2">Category
              <select value={expForm.category} onChange={(e)=>setExpForm({...expForm, category:e.target.value})}
                      className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80">
                {['petty_cash','materials','transport','meals','other'].map(c=><option key={c} value={c}>{c.replace('_',' ')}</option>)}
              </select>
            </label>
            <label className="block text-sm mb-2">Description
              <input required value={expForm.description} onChange={(e)=>setExpForm({...expForm, description:e.target.value})} data-testid="exp-desc"
                     className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
            </label>
            <label className="block text-sm mb-4">Amount (AED)
              <input type="number" required value={expForm.amount_aed} onChange={(e)=>setExpForm({...expForm, amount_aed:+e.target.value})} data-testid="exp-amount"
                     className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={()=>setAddingExp(false)} className="btn-ghost text-sm">Cancel</button>
              <button disabled={busy} type="submit" className="btn-primary text-sm" data-testid="exp-submit">Log</button>
            </div>
          </form>
        </div>
      )}
      {viewInv && <InvoiceModal invoiceId={viewInv} onClose={() => setViewInv(null)} onUpdate={load}/>}
      {viewVendor && <VendorModal vendorId={viewVendor} onClose={() => setViewVendor(null)} onOpenInvoice={(id) => { setViewVendor(null); setViewInv(id); }}/>}
    </div>
  );
}
